import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Person as PersonIcon,
  Lock as LockIcon,
  Email as EmailIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Business as BusinessIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { useAuth } from '../contexts/AuthContext';
import AuthService from '../services/AuthService';

const LoginPage = ({ colors, onNavigateToRegister }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [localError, setLocalError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Forgot password dialog state
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetEmailError, setResetEmailError] = useState('');
  const [isResetting, setIsResetting] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  // Email verification state
  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const [resendingVerification, setResendingVerification] = useState(false);
  const [verificationResent, setVerificationResent] = useState(false);

  // Tenant-selection state -- shown instead of an error when the submitted
  // email matches more than one tenant (AuthService.login /
  // AuthenticationService.login on the backend). Each tenant's account for
  // this email can have its OWN password (see project_account_creation_improvements
  // item C -- email is unique per-tenant, not platform-wide), so picking a
  // tenant re-prompts for that tenant's own password rather than blindly
  // reusing whatever was typed on the first screen -- real bug found live
  // 2026-09-22: reusing the first password meant only the one tenant whose
  // password happened to match what was typed could ever be reached.
  const [tenantPickerOpen, setTenantPickerOpen] = useState(false);
  const [tenantOptions, setTenantOptions] = useState([]);
  const [isSelectingTenant, setIsSelectingTenant] = useState(false);
  const [selectedTenantOption, setSelectedTenantOption] = useState(null);
  const [tenantPassword, setTenantPassword] = useState('');
  const [tenantPasswordError, setTenantPasswordError] = useState('');
  const [showTenantPassword, setShowTenantPassword] = useState(false);

  const { login, error: authError, clearError } = useAuth();
  const navigate = useNavigate();

  const attemptLogin = async (tenantId, passwordOverride) => {
    const result = await login(username, passwordOverride !== undefined ? passwordOverride : password, tenantId);

    if (result.requiresTenantSelection) {
      setTenantOptions(result.tenantOptions || []);
      setTenantPickerOpen(true);
      return result;
    }

    // Added 2026-09-29 (bulk user onboarding): valid credentials, but this is
    // a bulk-onboarded user still on the shared default password -- send
    // them straight into the EXISTING reset-password flow with the
    // already-issued token, exactly as if they'd clicked a real reset email,
    // instead of showing a login error.
    if (result.requiresPasswordChange) {
      if (result.resetToken) {
        navigate(`/reset-password?token=${encodeURIComponent(result.resetToken)}`);
      } else {
        setLocalError('Use your welcome/reset email link to set your password. You can request a new link below.');
        setResetEmail(username);
        setForgotPasswordOpen(true);
      }
      return result;
    }

    if (!result.success) {
      // Check if error is email not verified
      const errorMsg = result.error || 'Login failed';
      if (errorMsg.toLowerCase().includes('verify your email') ||
          errorMsg.toLowerCase().includes('email not verified')) {
        setEmailNotVerified(true);
      }
      setLocalError(errorMsg);
    }
    return result;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError('');
    setEmailNotVerified(false);
    setVerificationResent(false);
    setTenantPickerOpen(false);
    clearError();

    // Validation
    if (!username.trim()) {
      setLocalError('Email is required');
      return;
    }
    if (!password) {
      setLocalError('Password is required');
      return;
    }

    setIsSubmitting(true);
    await attemptLogin();
    setIsSubmitting(false);
  };

  const handleTenantOptionClick = (option) => {
    // Pre-fill with whatever was typed on the first screen as a convenience
    // (it's correct for the common single-tenant case and for a user who
    // happens to reuse the same password across their accounts), but the
    // field stays editable and this tenant's own password is what actually
    // gets checked -- see the state comment above for why.
    setSelectedTenantOption(option);
    setTenantPassword(password);
    setTenantPasswordError('');
  };

  const handleTenantPasswordSubmit = async (e) => {
    e.preventDefault();
    if (!tenantPassword) {
      setTenantPasswordError('Password is required');
      return;
    }
    setIsSelectingTenant(true);
    setTenantPasswordError('');
    const result = await attemptLogin(selectedTenantOption.tenantId, tenantPassword);
    setIsSelectingTenant(false);
    if (result && !result.success && !result.requiresTenantSelection && !result.requiresPasswordChange) {
      setTenantPasswordError(result.error || 'Invalid password for this company');
    }
  };

  const handleBackToTenantList = () => {
    setSelectedTenantOption(null);
    setTenantPassword('');
    setTenantPasswordError('');
  };

  const handleTenantPickerClose = () => {
    setTenantPickerOpen(false);
    setTenantOptions([]);
    setSelectedTenantOption(null);
    setTenantPassword('');
    setTenantPasswordError('');
  };

  const handleResendVerification = async () => {
    if (!username.trim()) {
      setLocalError('Please enter your email first');
      return;
    }

    setResendingVerification(true);
    try {
      await AuthService.resendVerification(username);
      setVerificationResent(true);
      setLocalError('');
    } catch (err) {
      setLocalError(err.message || 'Failed to resend verification email');
    } finally {
      setResendingVerification(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSubmit(e);
    }
  };

  // Forgot password handlers
  const handleForgotPasswordOpen = () => {
    setForgotPasswordOpen(true);
    setResetEmail('');
    setResetEmailError('');
    setResetSuccess(false);
  };

  const handleForgotPasswordClose = () => {
    setForgotPasswordOpen(false);
    setResetEmail('');
    setResetEmailError('');
    setResetSuccess(false);
  };

  const handleResetPassword = async () => {
    setResetEmailError('');

    // Validate email
    if (!resetEmail.trim()) {
      setResetEmailError('Email address is required');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(resetEmail.trim())) {
      setResetEmailError('Please enter a valid email address');
      return;
    }

    setIsResetting(true);

    // Real bug fixed 2026-09-22: this was a pure stub (commented-out API call,
    // fake delay, always "succeeded") -- it never reached the backend at all.
    try {
      await AuthService.requestPasswordReset(resetEmail.trim());
      setResetSuccess(true);
    } catch (err) {
      setResetEmailError(err.message || 'Failed to send reset email. Please try again.');
    } finally {
      setIsResetting(false);
    }
  };

  const error = localError || authError;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: colors.background,
        p: 2,
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 400,
          bgcolor: colors.surface,
          border: `1px solid ${colors.border}`,
          p: 4,
        }}
      >
        {/* Logo / Brand */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography
            variant="h4"
            sx={{
              color: '#22c55e',
              fontWeight: 700,
              letterSpacing: '-0.025em',
              mb: 1,
            }}
          >
            Foysonis WMS
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: colors.textSecondary }}
          >
            Explore. Discover. Optimize.
          </Typography>
        </Box>

        {/* Verification Resent Success */}
        {verificationResent && (
          <Alert
            severity="success"
            sx={{
              mb: 3,
              borderRadius: 0,
              bgcolor: 'rgba(34, 197, 94, 0.1)',
              borderLeft: '4px solid #22c55e',
              '& .MuiAlert-icon': {
                color: '#22c55e',
              },
            }}
          >
            Verification email sent! Please check your inbox.
          </Alert>
        )}

        {/* Error Alert */}
        {error && !verificationResent && (
          <Alert
            severity={emailNotVerified ? 'warning' : 'error'}
            sx={{
              mb: emailNotVerified ? 1 : 3,
              borderRadius: 0,
              bgcolor: emailNotVerified ? 'rgba(245, 158, 11, 0.1)' : 'rgba(239, 68, 68, 0.1)',
              borderLeft: emailNotVerified ? '4px solid #f59e0b' : '4px solid #ef4444',
              '& .MuiAlert-icon': {
                color: emailNotVerified ? '#f59e0b' : '#ef4444',
              },
            }}
          >
            {error}
          </Alert>
        )}

        {/* Resend Verification Button */}
        {emailNotVerified && !verificationResent && (
          <Box sx={{ mb: 3, textAlign: 'center' }}>
            <Button
              variant="text"
              onClick={handleResendVerification}
              disabled={resendingVerification}
              sx={{
                color: '#22c55e',
                textTransform: 'none',
                '&:hover': {
                  bgcolor: 'rgba(34, 197, 94, 0.08)',
                },
              }}
            >
              {resendingVerification ? (
                <>
                  <CircularProgress size={16} sx={{ color: '#22c55e', mr: 1 }} />
                  Sending...
                </>
              ) : (
                'Resend verification email'
              )}
            </Button>
          </Box>
        )}

        {/* Login Form */}
        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isSubmitting}
            autoFocus
            autoComplete="email"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon sx={{ color: colors.textMuted }} />
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                bgcolor: colors.background,
                '& fieldset': {
                  borderColor: colors.borderLight,
                },
                '&:hover fieldset': {
                  borderColor: '#22c55e',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#22c55e',
                },
              },
              '& .MuiInputLabel-root': {
                color: colors.textSecondary,
              },
              '& .MuiInputBase-input': {
                color: colors.textPrimary,
              },
            }}
          />

          <TextField
            fullWidth
            label="Password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isSubmitting}
            autoComplete="current-password"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon sx={{ color: colors.textMuted }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    sx={{ color: colors.textMuted }}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 1.5,
              '& .MuiOutlinedInput-root': {
                bgcolor: colors.background,
                '& fieldset': {
                  borderColor: colors.borderLight,
                },
                '&:hover fieldset': {
                  borderColor: '#22c55e',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#22c55e',
                },
              },
              '& .MuiInputLabel-root': {
                color: colors.textSecondary,
              },
              '& .MuiInputBase-input': {
                color: colors.textPrimary,
              },
            }}
          />

          {/* Forgot Password Link */}
          <Box sx={{ textAlign: 'right', mb: 2 }}>
            <Link
              component="button"
              type="button"
              onClick={handleForgotPasswordOpen}
              sx={{
                color: colors.textSecondary,
                fontSize: '0.85rem',
                textDecoration: 'none',
                cursor: 'pointer',
                '&:hover': {
                  color: '#22c55e',
                  textDecoration: 'underline',
                },
              }}
            >
              Forgot Password?
            </Link>
          </Box>

          <Button
            fullWidth
            variant="contained"
            type="submit"
            disabled={isSubmitting}
            sx={{
              bgcolor: '#22c55e',
              color: colors.background,
              py: 1.5,
              fontWeight: 600,
              letterSpacing: '0.1em',
              '&:hover': {
                bgcolor: '#16a34a',
              },
              '&.Mui-disabled': {
                bgcolor: colors.surfaceElevated,
                color: colors.textMuted,
              },
            }}
          >
            {isSubmitting ? (
              <CircularProgress size={24} sx={{ color: colors.background }} />
            ) : (
              'Sign In'
            )}
          </Button>
        </Box>

        {/* Create account link */}
        <Box
          sx={{
            mt: 3,
            pt: 3,
            borderTop: `1px solid ${colors.border}`,
            textAlign: 'center',
          }}
        >
          <Typography variant="body2" sx={{ color: colors.textSecondary }}>
            Don't have an account?{' '}
            <Link
              component="button"
              type="button"
              onClick={onNavigateToRegister}
              sx={{
                color: '#22c55e',
                textDecoration: 'none',
                fontWeight: 600,
                cursor: 'pointer',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              Create Account
            </Link>
          </Typography>
        </Box>
      </Box>

      {/* Forgot Password Dialog */}
      <Dialog
        open={forgotPasswordOpen}
        onClose={handleForgotPasswordClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: colors.surface,
            borderRadius: 0,
            border: `1px solid ${colors.border}`,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            bgcolor: colors.surface,
            borderBottom: `1px solid ${colors.border}`,
            py: 1.5,
            px: 2,
          }}
        >
          <Typography variant="subtitle1" sx={{ color: colors.textPrimary, fontWeight: 600 }}>
            Reset Password
          </Typography>
          <IconButton
            onClick={handleForgotPasswordClose}
            sx={{ color: colors.textSecondary }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ bgcolor: colors.background, pt: 3, pb: 2 }}>
          {resetSuccess ? (
            // Success state
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <CheckCircleIcon sx={{ fontSize: 64, color: '#22c55e', mb: 2 }} />
              <Typography variant="h6" sx={{ color: colors.textPrimary, mb: 1 }}>
                Check Your Email
              </Typography>
              <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                We've sent a password reset link to:
              </Typography>
              <Typography variant="body2" sx={{ color: '#22c55e', fontWeight: 600, mt: 1 }}>
                {resetEmail}
              </Typography>
              <Typography variant="caption" sx={{ color: colors.textMuted, display: 'block', mt: 2 }}>
                If you don't see the email, check your spam folder.
              </Typography>
            </Box>
          ) : (
            // Form state
            <>
              <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 3 }}>
                Enter your email address and we'll send you a link to reset your password.
              </Typography>

              {resetEmailError && (
                <Alert
                  severity="error"
                  sx={{
                    mb: 2,
                    borderRadius: 0,
                    bgcolor: 'rgba(239, 68, 68, 0.1)',
                    borderLeft: '4px solid #ef4444',
                    '& .MuiAlert-icon': {
                      color: '#ef4444',
                    },
                  }}
                >
                  {resetEmailError}
                </Alert>
              )}

              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                disabled={isResetting}
                autoFocus
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon sx={{ color: colors.textMuted }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: colors.surface,
                    '& fieldset': {
                      borderColor: colors.borderLight,
                    },
                    '&:hover fieldset': {
                      borderColor: '#22c55e',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#22c55e',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: colors.textSecondary,
                  },
                  '& .MuiInputBase-input': {
                    color: colors.textPrimary,
                  },
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleResetPassword();
                  }
                }}
              />
            </>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            bgcolor: colors.surface,
            borderTop: `1px solid ${colors.border}`,
            px: 2,
            py: 1.5,
          }}
        >
          {resetSuccess ? (
            <Button
              fullWidth
              onClick={handleForgotPasswordClose}
              sx={{
                color: colors.textPrimary,
                border: `1px solid ${colors.border}`,
                '&:hover': {
                  bgcolor: colors.hoverBg,
                  borderColor: '#22c55e',
                },
              }}
            >
              Back to Login
            </Button>
          ) : (
            <>
              <Button
                onClick={handleForgotPasswordClose}
                sx={{
                  color: colors.textSecondary,
                  '&:hover': {
                    bgcolor: colors.hoverBg,
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleResetPassword}
                disabled={isResetting}
                sx={{
                  bgcolor: '#22c55e',
                  color: colors.background,
                  px: 3,
                  '&:hover': {
                    bgcolor: '#16a34a',
                  },
                  '&.Mui-disabled': {
                    bgcolor: colors.surfaceElevated,
                    color: colors.textMuted,
                  },
                }}
              >
                {isResetting ? (
                  <CircularProgress size={20} sx={{ color: colors.background }} />
                ) : (
                  'Send Reset Link'
                )}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Tenant Selection Dialog -- shown when the email matches 2+ tenants */}
      <Dialog
        open={tenantPickerOpen}
        onClose={isSelectingTenant ? undefined : handleTenantPickerClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: colors.surface,
            borderRadius: 0,
            border: `1px solid ${colors.border}`,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            bgcolor: colors.surface,
            borderBottom: `1px solid ${colors.border}`,
            py: 1.5,
            px: 2,
          }}
        >
          <Typography variant="subtitle1" sx={{ color: colors.textPrimary, fontWeight: 600 }}>
            Choose a Company
          </Typography>
          <IconButton
            onClick={handleTenantPickerClose}
            disabled={isSelectingTenant}
            sx={{ color: colors.textSecondary }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ bgcolor: colors.background, pt: 3, pb: 2 }}>
          {!selectedTenantOption ? (
            <>
              <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 2 }}>
                This email is used by more than one company. Select which one you'd like to sign in to.
              </Typography>
              <List sx={{ p: 0 }}>
                {tenantOptions.map((option) => (
                  <ListItemButton
                    key={option.tenantId}
                    onClick={() => handleTenantOptionClick(option)}
                    sx={{
                      mb: 1,
                      border: `1px solid ${colors.borderLight}`,
                      bgcolor: colors.surface,
                      '&:hover': {
                        borderColor: '#22c55e',
                        bgcolor: 'rgba(34, 197, 94, 0.08)',
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <BusinessIcon sx={{ color: colors.textMuted }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={option.tenantName}
                      primaryTypographyProps={{ sx: { color: colors.textPrimary, fontWeight: 500 } }}
                    />
                    <ChevronRightIcon sx={{ color: colors.textMuted }} />
                  </ListItemButton>
                ))}
              </List>
            </>
          ) : (
            <Box component="form" onSubmit={handleTenantPasswordSubmit}>
              <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 0.5 }}>
                Signing in to
              </Typography>
              <Typography variant="subtitle2" sx={{ color: colors.textPrimary, fontWeight: 600, mb: 2 }}>
                {selectedTenantOption.tenantName}
              </Typography>

              {tenantPasswordError && (
                <Alert severity="error" sx={{ mb: 2 }}>{tenantPasswordError}</Alert>
              )}

              <TextField
                fullWidth
                autoFocus
                label="Password"
                type={showTenantPassword ? 'text' : 'password'}
                value={tenantPassword}
                onChange={(e) => { setTenantPassword(e.target.value); setTenantPasswordError(''); }}
                disabled={isSelectingTenant}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon sx={{ color: colors.textMuted, fontSize: 20 }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowTenantPassword(!showTenantPassword)} edge="end" size="small">
                        {showTenantPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />

              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  onClick={handleBackToTenantList}
                  disabled={isSelectingTenant}
                  sx={{ color: colors.textSecondary }}
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={isSelectingTenant}
                  sx={{
                    bgcolor: '#22c55e',
                    color: '#09090b',
                    '&:hover': { bgcolor: '#16a34a' },
                  }}
                >
                  {isSelectingTenant ? <CircularProgress size={20} sx={{ color: '#09090b' }} /> : 'Sign In'}
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default LoginPage;
